import { CheckCircle } from 'iconoir-react';
import { FC } from 'react';
import { useTheme } from 'styled-components';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import {
  Button,
  ButtonLink,
} from '@savgroup-front-common/core/atoms/button';
import { Card } from '@savgroup-front-common/core/atoms/Cards';
import { Heading } from '@savgroup-front-common/core/atoms/Heading';
import { Tag } from '@savgroup-front-common/core/atoms/Tag';
import {
  TAG_TEXT_TRANSFORM,
  TAG_VARIANT,
} from '@savgroup-front-common/core/atoms/Tag/Tag.types';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { SafeFormattedMessageWithId } from '@savgroup-front-common/core/formatters/SafeFormattedMessageWithId';
import { useIsSuperAdmin } from '@savgroup-front-common/core/hooks';
import { PRICING_PLAN } from '@savgroup-front-common/types';

import { useAppContext } from '../../../../app/App.context';
import { PlanDto } from '../ChoosePlan.types';

import { ChoosePlanButton } from './ChoosePlanButton/ChoosePlanButton';
import messages from './messages';
import usePlanPreview from './PlanPreview.hooks';
import {
  $PlanPreview,
  $PlanPreviewTitleContainer,
  $PreviewFeatureContainer,
  $PreviewFeatureHeading,
  $TagIconContainer,
} from './PlanPreview.styles';
import { PlanPrice } from './PlanPrice/PlanPrice';
import { PreviewFeature } from './PreviewFeature/PreviewFeature';

interface PlanPreviewProps {
  plan: PlanDto;
  isFromScratch?: boolean;
}

const CONTACT_SALES_URL =
  'https://meetings.hubspot.com/clement-ducrocq?uuid=ec8f0cd1-4f01-4f7c-9bb4-1dda224ff0b8';

export const PlanPreview: FC<PlanPreviewProps> = ({ plan, isFromScratch }) => {
  const { selectedSeller } = useAppContext();
  const isSuperAdmin = useIsSuperAdmin();
  const {
    handleSetPlan,
    isAddContractLoading,
    handleActivatePricingPlan,
    isActivateLoading,
  } = usePlanPreview(isFromScratch || false);
  const theme = useTheme();

  const isFreemiumPlan = plan.pricingPlan === PRICING_PLAN.FREEMIUM;

  const isAlreadySubscribedToThisPlan =
    selectedSeller?.pricingPlan === plan.pricingPlan;

  return (
    <Card withoutMarginBottom withoutMarginTop>
      <$PlanPreview>
        <$PlanPreviewTitleContainer>
          <Heading level={4} withoutMargin>
            <SafeFormattedMessageWithId id={`pricing.${plan.name}`} />
          </Heading>
          {isSuperAdmin &&
            (!isAlreadySubscribedToThisPlan ? (
              <Button
                type={BUTTON_TYPES.BUTTON}
                tertiary
                onClick={() => handleSetPlan(plan.pricingPlan)}
                isLoading={isAddContractLoading}
              >
                <SafeFormattedMessageWithoutSpread
                  message={messages.addContract}
                />
              </Button>
            ) : (
              <Tag
                variant={TAG_VARIANT.SUCCESS}
                withoutMargin
                textTransform={TAG_TEXT_TRANSFORM.CAPITALIZE}
              >
                <$TagIconContainer>
                  <CheckCircle color={theme.colors.success} />
                  <SafeFormattedMessageWithoutSpread
                    message={messages.isActivePlan}
                  />
                </$TagIconContainer>
              </Tag>
            ))}
        </$PlanPreviewTitleContainer>

        <PlanPrice plan={plan} />

        {isFreemiumPlan && (
          <div>
            <Button
              type={BUTTON_TYPES.BUTTON}
              tertiary
              onClick={() => handleActivatePricingPlan(plan.pricingPlan)}
              isLoading={isActivateLoading}
            >
              <SafeFormattedMessageWithoutSpread message={messages.freePlan} />
            </Button>
          </div>
        )}

        {!isFreemiumPlan && (
          <div>
            {plan.needToContactSales && (
              <ButtonLink
                primary
                isExternal
                href={CONTACT_SALES_URL}
                target="_blank"
                isLoading={isActivateLoading}
              >
                <SafeFormattedMessageWithoutSpread
                  message={messages.contactSales}
                />
              </ButtonLink>
            )}

            {!plan.needToContactSales && (
              <ChoosePlanButton
                plan={plan}
                isLoading={isActivateLoading}
                onActivatePricingPlan={() =>
                  handleActivatePricingPlan(plan.pricingPlan)
                }
              />
            )}
          </div>
        )}

        <div>
          <$PreviewFeatureHeading>
            <SafeFormattedMessageWithId
              id={`pricing.${plan.name}.previewFeaturesTitle`}
            />
            :
          </$PreviewFeatureHeading>

          <$PreviewFeatureContainer>
            {plan.previewFeatures.map((previewFeature) => {
              return (
                <PreviewFeature
                  key={previewFeature.name}
                  previewFeature={previewFeature}
                  planName={plan.name}
                />
              );
            })}
          </$PreviewFeatureContainer>
        </div>
      </$PlanPreview>
    </Card>
  );
};
